/**
 * @render react
 * @name NextPages
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';
import { Rating } from 'Components/molecules/Rating';

import './_style.scss';

/**
 * NextPages component
 *
 * @param {Object} props - the props object
 * @param {string} props.title - The title of the section
 * @param {array} props.content - Array to render all the list items
 * @param {boolean} props.showIndex - Boolean to be passed to the List to show the index numbers
 * @param {boolean} props.showRating - Boolean to be passed to the List to show the rating
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling
 * @param {Object} props.extraClassNames - Extra classes to be added to the component
 * @param {Object} props.context - Extra information coming from Polaris provider
 * @param {string} props.currentlyReadingLabel - The label for the currently reading indicator
 */

const NextPages = ({
  content,
  context: { cssPrefix },
  currentlyReadingLabel,
  currentPageUrl,
  extraClassNames,
  loadStyles,
  ratingPosition,
  showIcon,
  showIndex,
  showRating,
  title,
}) => {
  const ratingBlock = ({ rating, position }) => {
    if (showRating && rating !== null && rating > 0) {
      return (
        <Rating
          rating={rating}
          extraClassNames={{
            [`${cssPrefix}__next-pages--item-rating`]: true,
            [`${cssPrefix}__next-pages--item-rating--${position}`]: position,
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className={classNames({
      [`${cssPrefix}__next-pages`]: loadStyles,
      ...extraClassNames,
    })}
    >
      {title && (
        <Heading size={6}>{title}</Heading>
      )}
      {content && (

        <ul className={classNames({
          [`${cssPrefix}__list`]: true,
          [`${cssPrefix}__next-pages--list`]: true,
          '-show-index': showIndex,
          '-show-icon': showIcon,
        })}
        >
          {content.map((singleContent, index) => {
            const currentlyReadingBlock = singleContent.url === currentPageUrl && (
              <span className={`${cssPrefix}__next-pages--currently-reading`}>{currentlyReadingLabel}</span>
            );
            return (
              <li
                key={index}
                id={`${cssPrefix}__next-pages--list-item_${index}`}
                className={classNames({
                  [`${cssPrefix}__list-item`]: true,
                  [`${cssPrefix}__next-pages--item`]: true,
                  '-current': singleContent.url === currentPageUrl,
                })}
              >
                {singleContent.url && (
                <Link
                  extraClassNames={{
                    [`${cssPrefix}__next-pages--item-link`]: true,
                  }}
                  href={singleContent.url}
                >
                  {showIndex && !showIcon && <span className={`${cssPrefix}__list-item--index`}>{`${index + 1}`}</span>}
                  {showIcon && !showIndex && (
                  <div className={`${cssPrefix}__next-pages--item-icon--wrapper`}>
                    <div className={`${cssPrefix}__next-pages--item-icon`} />
                  </div>
                  )}
                  <div className={`${cssPrefix}__next-pages--item-texts`}>
                    <span className={`${cssPrefix}__next-pages--item-title`}>
                      {singleContent.title}
                      {ratingPosition === 'after-title' ? (
                        <div className={`${cssPrefix}__next-pages--rating-and-status-wrapper`}>
                          {ratingBlock({ rating: singleContent.rating, position: ratingPosition })}
                          {showRating && singleContent.rating !== null && singleContent.rating > 0 && currentlyReadingBlock && ' · '}
                          {currentlyReadingBlock}
                        </div>
                      ) : currentlyReadingBlock}
                    </span>

                    {singleContent.subtitle && (
                    <span className={`${cssPrefix}__next-pages--item-subtitle`}>
                      {singleContent.subtitle}
                    </span>
                    )}
                  </div>
                  {ratingPosition === 'right' && ratingBlock({ rating: singleContent.rating, position: ratingPosition })}
                </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

NextPages.propTypes = {
  content: PropTypes.arrayOf(PropTypes.shape()),
  context: PropTypes.shape(),
  currentlyReadingLabel: PropTypes.string,
  currentPageUrl: PropTypes.string,
  extraClassNames: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  ratingPosition: PropTypes.string,
  showIcon: PropTypes.bool,
  showIndex: PropTypes.bool,
  showRating: PropTypes.bool,
  title: PropTypes.string,
};

NextPages.defaultProps = {
  content: [],
  context: {},
  currentlyReadingLabel: ' - currently reading',
  currentPageUrl: '',
  extraClassNames: {},
  loadStyles: true,
  ratingPosition: 'right',
  showIcon: false,
  showIndex: true,
  showRating: false,
  title: '',
};

export default withPolaris(NextPages);
